"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { getProducts, type ProductDisplay } from "@/lib/api/dutchie";
import { createProductSlug } from "@/lib/slug-utils";
import { useCart } from "@/lib/context/cart-context";
import { Plus, Sparkles } from "lucide-react";

/**
 * Cart-drawer upsell: given whatever's in the cart, recommends 4
 * related products the shopper hasn't added yet. Ranks by:
 *   1. Same category as any cart item (soft-match)
 *   2. Same brand as any cart item
 *   3. Falls back to "on special" and popular items
 *
 * Renders as a horizontal scroll row above the Order Summary so the
 * shopper sees "one more thing" right before they check out.
 *
 * When the /api/upsell endpoint eventually exists (Kushmart pattern),
 * this component swaps the local ranking for a server-side call. For
 * now, everything runs client-side from products already in memory.
 */
export default function CartUpsell() {
  const { cart, addToCart } = useCart();
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getProducts();
        if (!cancelled) setProducts(p);
      } catch {
        // Silent, just no upsell.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recommendations = useMemo(() => {
    if (!products.length || !cart.items.length) return [] as ProductDisplay[];

    const inCartIds = new Set(cart.items.map((i) => i.productId));
    const cartCategories = new Set<string>();
    const cartBrands = new Set<string>();

    for (const it of cart.items) {
      const source = products.find((p) => p.id === it.productId);
      if (!source) continue;
      if (source.category) cartCategories.add(String(source.category).toLowerCase());
      const b: any = source.brand;
      const brandName = typeof b === "string" ? b : b?.name;
      if (brandName) cartBrands.add(String(brandName).toLowerCase());
    }

    function brandOf(p: ProductDisplay): string {
      const b: any = p.brand;
      return typeof b === "string" ? b : b?.name || "";
    }

    const scored = products
      .filter((p) => !inCartIds.has(p.id))
      .filter((p) => p.inStock !== false)
      .map((p) => {
        const cat = (p.category || "").toLowerCase();
        const brand = brandOf(p).toLowerCase();
        let score = 0;
        if (cat && cartCategories.has(cat)) score += 3;
        if (brand && cartBrands.has(brand)) score += 5;
        // Gentle boost for products on special
        if ((p as any).specialPrice && (p as any).specialPrice < p.price) score += 1;
        return { p, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.p);

    return scored;
  }, [products, cart.items]);

  if (!loaded || !cart.items.length || recommendations.length === 0) return null;

  return (
    <div className="mt-4 mb-2">
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <Sparkles size={14} className="text-primary_green" />
        <h3 className="text-sm font-bold text-gray-900">You may also like</h3>
        <span className="text-[10px] text-gray-400 ml-auto">Based on what&apos;s in your cart</span>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
        {recommendations.map((p) => {
          const anyP: any = p;
          const brandName =
            typeof p.brand === "string" ? p.brand : (p.brand as any)?.name || "";
          const price = anyP.specialPrice ?? anyP.price ?? 0;
          const slug = createProductSlug(p);
          return (
            <div
              key={p.id}
              className="w-32 shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition"
            >
              <Link href={`/shop/product/${slug}`} className="block">
                <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                  {p.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-gray-300 text-xs">no image</div>
                  )}
                </div>
                <div className="p-2">
                  {brandName && (
                    <p className="text-[10px] text-primary_green font-semibold truncate">{brandName}</p>
                  )}
                  <p className="text-xs font-medium text-gray-900 line-clamp-2 leading-snug">{p.name}</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">${Number(price).toFixed(2)}</p>
                </div>
              </Link>
              <button
                onClick={() => addToCart(p, 1)}
                className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-primary_green hover:bg-primary_green hover:text-white py-1.5 border-t border-gray-100 transition"
              >
                <Plus size={12} />
                Add
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
